import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Filter, Loader2, RefreshCw, Search, ShieldAlert } from "lucide-react";
import { StatCard } from "../components/RegisterPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { apiFetch, ApiError } from "../lib/api";

const badgeStyle = (eventType) => {
  if (/reject|fail|denied|locked/i.test(eventType)) return { background: "#FEE2E2", color: "#7F1D1D" };
  if (/approve|success|created/i.test(eventType)) return { background: "#DCFCE7", color: "#166534" };
  if (/security|permission|credential/i.test(eventType)) return { background: "#DBEAFE", color: "#1E40AF" };
  return { background: "#F1F5F9", color: "#334155" };
};

export default function ActivityLog() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [filters, setFilters] = useState({ q: "", module: "", eventType: "", from: "", to: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setForbidden(false);
    const query = new URLSearchParams({ limit: "200" });
    Object.entries(filters).forEach(([key, value]) => { if (value) query.set(key, value); });
    try {
      const result = await apiFetch(`/audit-events?${query}`);
      setEvents(Array.isArray(result?.events) ? result.events : []);
    } catch (requestError) {
      setForbidden(requestError instanceof ApiError && requestError.status === 403);
      setError(requestError.message || "Audit events could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  const users = useMemo(() => new Set(events.map((event) => event.actor?.username).filter(Boolean)).size, [events]);
  const modules = useMemo(() => new Set(events.map((event) => event.module).filter(Boolean)).size, [events]);
  const securityEvents = useMemo(() => events.filter((event) => /security/i.test(event.eventType)).length, [events]);

  return (
    <div className="space-y-5" data-testid="activity-log-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-[#082B4D]">Activity Log & Audit Trail</h1>
        <p className="text-sm text-slate-500 mt-1">Server-generated immutable events. Entries cannot be added, edited or deleted.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Visible Events" value={events.length} testid="log-total" />
        <StatCard label="Unique Users" value={users} tone="info" testid="log-users" />
        <StatCard label="Modules" value={modules} tone="safe" testid="log-modules" />
        <StatCard label="Security Events" value={securityEvents} testid="log-security" />
      </div>
      <div className="command-card p-4 space-y-3">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold"><Filter className="w-4 h-4" /> Audit filters</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
          <div className="relative lg:col-span-2"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input aria-label="Search audit events" className="pl-9" placeholder="User, record, event…" value={filters.q} onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))} /></div>
          <Input aria-label="Module filter" placeholder="Module" value={filters.module} onChange={(event) => setFilters((current) => ({ ...current, module: event.target.value }))} />
          <Input aria-label="Event type filter" placeholder="Event type" value={filters.eventType} onChange={(event) => setFilters((current) => ({ ...current, eventType: event.target.value }))} />
          <Input aria-label="From date" type="date" value={filters.from} onChange={(event) => setFilters((current) => ({ ...current, from: event.target.value }))} />
          <div className="flex gap-2"><Input aria-label="To date" type="date" value={filters.to} onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))} /><Button type="button" variant="outline" size="icon" onClick={load} title="Refresh"><RefreshCw className="w-4 h-4" /></Button></div>
        </div>
      </div>
      <div className="command-card overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center items-center gap-2 text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Loading immutable audit events…</div>
        ) : error ? (
          <div className="py-14 px-6 text-center"><ShieldAlert className="w-8 h-8 mx-auto text-amber-600" /><div className="font-semibold text-[#082B4D] mt-3">{forbidden ? "Audit access is not permitted" : "Audit events unavailable"}</div><p className="text-sm text-slate-500 mt-1">{error}</p></div>
        ) : events.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500">No audit events match the selected filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-[#082B4D] text-white"><tr>{["Timestamp", "Event", "User / Role", "Module", "Record", "Scope", "Correlation ID"].map((label) => <th key={label} className="px-3 py-3 font-semibold whitespace-nowrap">{label}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">{events.map((event) => (
                <tr key={event.id} className="hover:bg-slate-50 align-top">
                  <td className="px-3 py-3 whitespace-nowrap"><div className="font-medium text-slate-700">{new Date(event.timestamp).toLocaleString()}</div><div className="text-[10px] text-slate-400">{event.timezone || "UTC"}</div></td>
                  <td className="px-3 py-3"><span className="inline-flex rounded-full px-2 py-1 font-semibold whitespace-nowrap" style={badgeStyle(event.eventType)}>{event.eventType}</span></td>
                  <td className="px-3 py-3"><div className="font-semibold text-slate-700">{event.actor?.username || "System"}</div><div className="text-[10px] text-slate-400">{event.actor?.roles?.map((role) => role.name || role.code).join(", ") || "System context"}</div></td>
                  <td className="px-3 py-3 font-medium text-slate-700">{event.module}</td>
                  <td className="px-3 py-3 font-mono text-[11px]">{event.recordId || "—"}{event.recordVersion ? ` · v${event.recordVersion}` : ""}</td>
                  <td className="px-3 py-3 text-slate-600">{event.siteId ? `Site ${event.siteId}` : event.departmentId ? `Department ${event.departmentId}` : "Organization"}</td>
                  <td className="px-3 py-3 font-mono text-[10px] text-slate-500">{event.correlationId}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
